import React, { useState, useRef } from 'react';

const AdminPanel = ({ onLogout, onBack, darkMode, setDarkMode, rotationInterval, setRotationInterval }) => {
  const [uploadStatus, setUploadStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newReadOnlyPassword, setNewReadOnlyPassword] = useState('');
  const [newSamaritanPassword, setNewSamaritanPassword] = useState('');
  const [newMagicPassword, setNewMagicPassword] = useState('');
  const [newAshleyPassword, setNewAshleyPassword] = useState('');
  const [newAppliPassword, setNewAppliPassword] = useState('');
  const fileInputRef = useRef(null);

  const API_URL = '';

  const handleFileUpload = async (file) => {
    if (!file) return;

    const token = localStorage.getItem('authToken'); // Changed to authToken
    if (!token) {
      setUploadStatus('Not authenticated');
      return;
    }

    setLoading(true);
    setUploadStatus('Uploading...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setUploadStatus(`✓ Success! Processed ${data.count} events. Refresh to see updates.`);
        setTimeout(() => setUploadStatus(''), 5000);
      } else {
        setUploadStatus(`✗ Error: ${data.error}`);
      }
    } catch (err) {
      setUploadStatus(`✗ Upload failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
  };

  const handleSettingsUpdate = async () => {
    const token = localStorage.getItem('authToken'); // Changed from adminToken to authToken
    if (!token) return;

    try {
      const body = {
        darkMode,
        rotationInterval,
      };

      if (newAdminPassword) body.newAdminPassword = newAdminPassword;
      if (newReadOnlyPassword) body.newReadOnlyPassword = newReadOnlyPassword;
      if (newSamaritanPassword) body.newSamaritanPassword = newSamaritanPassword;
      if (newMagicPassword) body.newMagicPassword = newMagicPassword;
      if (newAshleyPassword) body.newAshleyPassword = newAshleyPassword;
      if (newAppliPassword) body.newAppliPassword = newAppliPassword;

      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        setUploadStatus('✓ Settings updated successfully!');
        setNewAdminPassword('');
        setNewReadOnlyPassword('');
        setNewSamaritanPassword('');
        setNewMagicPassword('');
        setNewAshleyPassword('');
        setNewAppliPassword('');
        setTimeout(() => setUploadStatus(''), 3000);
      } else {
        setUploadStatus(`✗ Error: ${data.error}`);
      }
    } catch (err) {
      setUploadStatus(`✗ Settings update failed: ${err.message}`);
    }
  }


  const handleClearData = async () => {
    if (!window.confirm('WARNING: This will delete ALL events from the database. This action cannot be undone.\n\nAre you sure?')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUploadStatus('✓ Database cleared successfully.');
        setTimeout(() => setUploadStatus(''), 3000);
      } else {
        setUploadStatus(`✗ Error: ${data.error}`);
      }
    } catch (err) {
      setUploadStatus(`✗ Failed to clear data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="calendar-container" style={{ overflowY: 'auto', backgroundColor: '#f9fafb' }}>
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>System Administration</h2>
            <p style={{ color: '#6b7280' }}>Manage calendar data and system settings</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button onClick={onBack} className="btn btn-secondary">← Back to Calendar</button>
            <button onClick={onLogout} className="btn btn-secondary" style={{ borderColor: '#fca5a5', color: '#b91c1c', backgroundColor: '#fef2f2' }}>Sign Out</button>
          </div>
        </div>

        {uploadStatus && (
          <div className={uploadStatus.startsWith('✓') ? 'alert-success' : 'alert-error'}>
            {uploadStatus}
          </div>
        )}

        <div className="dashboard-grid">
          {/* Left Column: Upload */}
          <div className="dashboard-card">
            <div className="card-title">
              <span>📄</span> Data Import
            </div>

            <div
              className={`upload-area ${dragOver ? 'drag-over' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <div className="upload-icon">☁️</div>
              <p className="upload-text-main">
                {loading ? 'Processing...' : 'Click to Upload CSV'}
              </p>
              <p className="upload-text-sub">
                or drag and drop file here
              </p>
            </div>
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
              Supported Format: Arive Export CSV
            </p>

            <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleClearData}
                style={{
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fca5a5',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  width: '100%'
                }}
              >
                ⚠️ Clear All Database Data
              </button>
            </div>
          </div>

          {/* Right Column: Settings */}
          <div className="dashboard-card">
            <div className="card-title">
              <span>⚙️</span> System Settings
            </div>

            <div className="form-group">
              <label className="form-label">Rotation Interval (Seconds)</label>
              <input
                type="number"
                className="form-input"
                value={rotationInterval}
                onChange={(e) => setRotationInterval(parseInt(e.target.value) || 20)}
                min="5"
                max="300"
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={(e) => setDarkMode(e.target.checked)}
                  style={{ width: 'auto', height: '1rem', width: '1rem' }}
                />
                Enable Dark Mode Display
              </label>
            </div>

            <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

            <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '1rem', color: '#4b5563' }}>Security & Access</h4>

            <div className="form-group">
              <label className="form-label">Change Admin Password</label>
              <input
                type="password"
                className="form-input"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="New admin password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Change Guest (Read-Only) Password</label>
              <input
                type="password"
                className="form-input"
                value={newReadOnlyPassword}
                onChange={(e) => setNewReadOnlyPassword(e.target.value)}
                placeholder="New guest password"
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 600, color: '#4b5563', marginTop: '1rem' }}>Team Passwords</label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Samaritan Mortgage</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newSamaritanPassword}
                    onChange={(e) => setNewSamaritanPassword(e.target.value)}
                    placeholder="Samaritan pass"
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Magic Mortgage</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newMagicPassword}
                    onChange={(e) => setNewMagicPassword(e.target.value)}
                    placeholder="Magic pass"
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Appli</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newAppliPassword}
                    onChange={(e) => setNewAppliPassword(e.target.value)}
                    placeholder="Appli pass"
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Appli - Ashley</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newAshleyPassword}
                    onChange={(e) => setNewAshleyPassword(e.target.value)}
                    placeholder="Ashley pass"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSettingsUpdate}
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              Save All Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
